import { createContext, useContext, useEffect } from "react";
import {
  Outlet,
  Link,
  NavLink,
  type LinkProps,
  type NavLinkProps,
} from "react-router-dom";
import { useTranslation } from "react-i18next";

export type Lang = "de" | "en";

const LangContext = createContext<Lang>("de");

export function useCurrentLang(): Lang {
  return useContext(LangContext);
}

/**
 * Baut aus einem basis-relativen Pfad (z. B. "/villas") die passende
 * sprachspezifische URL. Deutsch bleibt unverändert, Englisch erhält
 * das Präfix "/en".
 */
export function localizePath(path: string, lang: Lang): string {
  if (path === "/") return lang === "en" ? "/en" : "/";
  return lang === "en" ? `/en${path}` : path;
}

/**
 * Entfernt ein ggf. vorhandenes "/en"-Präfix, z. B. "/en/villas" -> "/villas".
 */
export function stripLangPrefix(path: string): string {
  if (path === "/en") return "/";
  if (path.startsWith("/en/")) return path.slice("/en".length);
  return path;
}

/**
 * Wrapper-Route, die die aktive Sprache anhand der verschachtelten Route
 * festlegt und alle Unterseiten mit dem passenden Sprach-Context versorgt.
 */
export function LanguageOutlet({ lang }: { lang: Lang }) {
  const { i18n } = useTranslation();

  useEffect(() => {
    if (i18n.language !== lang) {
      i18n.changeLanguage(lang);
    }
  }, [i18n, lang]);

  return (
    <LangContext.Provider value={lang}>
      <Outlet />
    </LangContext.Provider>
  );
}

/**
 * Sprachbewusster Ersatz für <Link>: fügt bei aktiver englischer Sprache
 * automatisch das "/en"-Präfix hinzu.
 */
export function LocalizedLink({ to, ...props }: LinkProps) {
  const lang = useCurrentLang();
  return <Link to={localizePath(to as string, lang)} {...props} />;
}

/**
 * Sprachbewusster Ersatz für <NavLink> (mit aktiv-Zustand).
 */
export function LocalizedNavLink({ to, ...props }: NavLinkProps) {
  const lang = useCurrentLang();
  return <NavLink to={localizePath(to as string, lang)} {...props} />;
}